import { getApiInstance } from '../axiosInterceptor'
import axios from 'axios'

/**
 * Get API endpoint URL
 * @returns {string} The base API URL
 */
const getApiEndpoint = () => {
    return process.env.REACT_APP_API_ENDPOINT || 'https://gw.dev.apim.kmart.com.au/kolumbus/api/v2'
};


// Global cache for generic settings with a pending request tracker
let genericSettingsCache = null
let genericSettingsLastFetched = null
const CACHE_EXPIRY_TIME = 60 * 60 * 1000 // 1 hour in milliseconds
let pendingGenericSettingsRequest = null

// Request deduplication tracker for other requests
const pendingRequests = {}

export const formatTransactionData = (data) => {
  const {
    transactionType,
    amount,
    invoiceNumber,
    expenseType,
    depositType,
    date,
    time,
    notes,
    storeId
  } = data

  const formattedDateTime = `${date.format('YYYY-MM-DD')} ${time.format('h:mm a')}`
  const transactionUsage = transactionType === 'out' ? expenseType : depositType

  const formattedData = {
    invoiceNumber: invoiceNumber || '',
    invoiceDateTime: formattedDateTime,
    cashFlowType: transactionType === 'out' ? 'cash-out' : 'cash-in',
    transactionUsage: transactionUsage || '',
    amount: amount,
    storeId: storeId
  }

  // Only add description if it exists and isn't empty
  if (notes && notes.trim() !== '') {
    formattedData.description = notes
  }

  return formattedData
}

export const createTransaction = async (transactionData, files, msalInstance) => {
  try {
    const formData = new FormData()

    // Add transaction data to form data
    formData.append('invoiceNumber', transactionData.invoiceNumber || '')
    formData.append('invoiceDateTime', transactionData.invoiceDateTime || '')

    // Only append description if it exists and isn't empty
    if (transactionData.description && transactionData.description.trim() !== '') {
      formData.append('description', transactionData.description)
    }

    formData.append('cashFlowType', transactionData.cashFlowType || '')
    formData.append('transactionUsage', transactionData.transactionUsage || '')
    formData.append('amount', transactionData.amount || '')
    formData.append('storeId', transactionData.storeId || '')

    // If there are files, add them with their original name (not as blobs)
    if (files && files.length > 0) {
      // Add the actual files available with their original names
      files.forEach((file, index) => {
        const fileObj = file.originFileObj || file
        const fieldName = `invoiceFile${index + 1}`
        formData.append(fieldName, fileObj, fileObj.name) // Include the original filename
      })
    }

    const apiUrl = `${getApiEndpoint()}/pettyCash/createEntries`

    // Explicitly set headers to ensure proper formatting
    const headers = {
      'Accept': 'application/json'
    }

    // If MSAL instance is provided, use it to get the API instance
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)

      const response = await axiosInstance.post(apiUrl, formData, {
        headers: headers
      })
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')

      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, formData, {
        headers: headers
      })

      return response.data
    }
  } catch (error) {
    console.error('Error creating transaction:', error)
    throw error
  }
}

/**
 * Generate a unique request key based on params
 * @param {Object} params - Query parameters
 * @returns {string} - Unique request key
 */
const getRequestKey = (params = {}) => {
  return `txn_${params.month || ''}_${params.year || ''}_${params.txnType || ''}_${params.status || ''}`
}

/**
 * UPDATED: Fetch transactions from the API with request deduplication and disclaimer support
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {Object} params - Query parameters object
 * @param {string} params.month - Month in format '01', '02', etc.
 * @param {string} params.year - Year in format 'YYYY'
 * @param {string} params.txnType - Transaction type (default: 'pettyCashTxn')
 * @param {string} params.status - Status filter (default: 'active')
 * @returns {Promise} - Promise that resolves with transaction data and raw API response
 */
export const fetchTransactions = async (msalInstance, params = {}) => {
  try {
    // Use provided params or defaults
    const queryParams = {
      month: params.month,
      year: params.year,
      txnType: params.txnType || 'pettyCashTxn',
      status: params.status || 'active'
    }

    // Create a unique key for this request
    const requestKey = getRequestKey(queryParams)

    // Check if a request with these exact parameters is already in progress
    if (pendingRequests[requestKey]) {
      return pendingRequests[requestKey]
    }

    // Build query string - properly encode parameters
    const queryString = Object.entries(queryParams)
      .filter(([value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')

    // Construct API URL with query string
    const apiUrl = `${getApiEndpoint()}/pettyCash/entries?${queryString}`

    // Get API instance and make the request
    const axiosInstance = getApiInstance(msalInstance)

    // Store the promise in our pending requests
    pendingRequests[requestKey] = axiosInstance.get(apiUrl)
      .then(response => {
        // Remove from pending requests after completion
        delete pendingRequests[requestKey]

        // NEW: Return both the processed data AND the raw API response
        // This allows the component to check for disclaimer messages
        return {
          data: response.data,
          rawResponse: response.data // Keep the raw response for disclaimer checking
        }
      })
      .catch(error => {
        // Remove from pending requests on error
        delete pendingRequests[requestKey]
        console.error('Error fetching transactions:', error)

        // NEW: Check if the error response contains the disclaimer message
        if (error.response && error.response.data && error.response.data.message) {
          const errorMessage = error.response.data.message

          // If it's the "previous month not closed" message, return it as a special response
          if (errorMessage.toLowerCase().includes('previous month cash balance is not closed')) {
            return {
              data: null, // No transaction data
              rawResponse: error.response.data, // Contains the disclaimer message
              isDisclaimer: true // Flag to indicate this is a disclaimer response
            }
          }
        }

        throw error
      })

    return pendingRequests[requestKey]
  } catch (error) {
    console.error('Error fetching transactions:', error)
    throw error
  }
}

/**
 * Fetch deleted transactions from the API
 * @param {Object} msalInstance - MSAL instance for authentication
 * @param {Object} params - Query parameters object
 * @param {string} params.month - Month in format '01', '02', etc.
 * @param {string} params.year - Year in format 'YYYY'
 * @returns {Promise} - Promise that resolves with deleted transaction data
 */
export const fetchDeletedTransactions = async (msalInstance, params = {}) => {
  try {
    // Set required parameters and status to 'deleted'
    const queryParams = {
      month: params.month,
      year: params.year,
      txnType: 'pettyCashTxn',
      status: 'deleted'
    }

    // Create a unique key for this request
    const requestKey = getRequestKey(queryParams)

    // Check if a request with these exact parameters is already in progress
    if (pendingRequests[requestKey]) {
      console.log('Returning existing deleted transactions request for:', requestKey)
      return pendingRequests[requestKey]
    }

    const queryString = Object.entries(queryParams)
      .filter(([value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')

    // Construct API URL with query string
    const apiUrl = `${getApiEndpoint()}/pettyCash/entries?${queryString}`

    // Get API instance and make the request
    const axiosInstance = getApiInstance(msalInstance)

    // Store the promise in our pending requests
    console.log('Making new deleted transactions request for:', requestKey)
    pendingRequests[requestKey] = axiosInstance.get(apiUrl)
      .then(response => {
        // Remove from pending requests after completion
        delete pendingRequests[requestKey]
        if (Array.isArray(response.data)) {
          return response.data
        }

        // Otherwise return the standard format
        return response.data
      })
      .catch(error => {
        // Remove from pending requests on error
        delete pendingRequests[requestKey]
        console.error('Error fetching deleted transactions:', error)
        throw error
      })

    return pendingRequests[requestKey]
  } catch (error) {
    console.error('Error fetching deleted transactions:', error)
    throw error
  }
}

/**
 * Delete a transaction using the API with transactionId as query parameter
 * @param {string} transactionId - ID of the transaction to delete
 * @param {Object} msalInstance - Optional MSAL instance for authentication
 * @returns {Promise} Promise that resolves with the deletion result
 */
export const deleteTransaction = async (transactionId, msalInstance) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    // Build query string with transactionId parameter
    const queryParams = new URLSearchParams({
      transactionId: transactionId
    })

    const apiUrl = `${getApiEndpoint()}/pettyCash/deleteTransaction?${queryParams.toString()}`

    // If MSAL instance is provided, use it to get the API instance
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.delete(apiUrl)
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')
      const response = await axios.delete(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      return response.data
    }
  } catch (error) {
    console.error('Error deleting transaction:', error)
    throw error
  }
}

/**
 * Fetch cash flow types with improved caching and request deduplication
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise} Promise that resolves with the types data
 */
export const fetchCashFlowTypes = async (msalInstance) => {
  try {
    // Check if we have a valid cache
    const now = Date.now()
    if (
      genericSettingsCache &&
      genericSettingsLastFetched &&
      (now - genericSettingsLastFetched) < CACHE_EXPIRY_TIME
    ) {
      console.log('Using cached generic settings')
      return genericSettingsCache
    }

    // If there's already a pending request, return that instead of making a new one
    if (pendingGenericSettingsRequest) {
      console.log('Returning pending generic settings request')
      return pendingGenericSettingsRequest
    }

    // Construct API URL without query parameters
    const apiUrl = `${getApiEndpoint()}/pettyCash/genericSettings`

    // Create the promise for the request
    const makeRequest = async () => {
      try {
        let response
        if (msalInstance) {
          const axiosInstance = getApiInstance(msalInstance)
          response = await axiosInstance.get(apiUrl)
        } else {
          // Fallback to using token from localStorage
          const token = localStorage.getItem('bearerToken')
          response = await axios.get(apiUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        }

        // Cache the response
        genericSettingsCache = response.data
        genericSettingsLastFetched = now

        // Clear the pending request
        pendingGenericSettingsRequest = null

        return response.data
      } catch (error) {
        // Clear the pending request on error too
        pendingGenericSettingsRequest = null
        throw error
      }
    }

    // Store the promise so other calls can use it
    pendingGenericSettingsRequest = makeRequest()
    return pendingGenericSettingsRequest
  } catch (error) {
    console.error('Error fetching generic settings:', error)
    throw error
  }
}

/**
 * Fetch attachments for a transaction
 * @param {string} transactionId - ID of the transaction
 * @param {Object} msalInstance - Optional MSAL instance for authentication
 * @returns {Promise} Promise that resolves with the attachments data
 */
export const fetchTransactionAttachments = async (transactionId) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    // This is a placeholder function for now
    // In a real implementation, you would call an API to fetch the attachments
    return Promise.resolve({
      success: true,
      data: []
    })
  } catch (error) {
    console.error('Error fetching transaction attachments:', error)
    throw error
  }
}

export const handleAttachments = async (txnId, actionType, attachmentId = null, newFile = null, msalInstance) => {
  try {
    if (!txnId) {
      throw new Error('Transaction ID is required')
    }

    if (!actionType || (actionType !== 'update' && actionType !== 'delete')) {
      throw new Error('Valid action type (update or delete) is required')
    }

    if (actionType === 'delete' && !attachmentId) {
      throw new Error('Attachment ID is required for delete operations')
    }

    if (actionType === 'update' && !newFile) {
      throw new Error('File is required for update operations')
    }

    const apiUrl = `${getApiEndpoint()}/generic/handleAttachments`

    // Create form data for the request
    const formData = new FormData()

    formData.append('txnId', txnId)
    formData.append('type', 'pettyCash')

    if (actionType === 'delete') {
      formData.append('actionType', 'delete')
      formData.append('attachmentId', attachmentId)
    } else if (actionType === 'update') {
      formData.append('actionType', 'update')
      formData.append('newFile', newFile, newFile.name) // Include original filename
    }

    // Make the API request with appropriate headers
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, formData, {
        headers: {
          'Accept': 'application/json'
          // Don't set Content-Type here, it will be set automatically for FormData
        }
      })
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
          // Don't set Content-Type here, it will be set automatically for FormData
        }
      })
      return response.data
    }
  } catch (error) {
    console.error('Error handling attachments:', error)
    throw error
  }
}

/**
 * Generate a pre-signed URL for an attachment
 * @param {string} attachmentS3Path - The S3 path of the attachment
 * @param {Object} msalInstance - MSAL instance for authentication
 * @returns {Promise<string>} - Promise that resolves with the pre-signed URL
 */
export const generatePresignedUrl = async (attachmentS3Path, msalInstance) => {
  try {
    if (!attachmentS3Path) {
      throw new Error('Attachment S3 path is required')
    }

    const apiUrl = `${getApiEndpoint()}/generic/generatePreSignedUrl`

    const payload = {
      attachmentS3Path: attachmentS3Path
    }

    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, payload)
      return response.data.preSignedUrl
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data.preSignedUrl
    }
  } catch (error) {
    console.error('Error generating pre-signed URL:', error)
    throw error
  }
}

export const updateTransactionDecision = async (transactionId, decision, msalInstance) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    // Validate decision value
    if (decision !== 'approve' && decision !== 'reject') {
      throw new Error('Decision must be either "approve" or "reject"')
    }

    const apiUrl = `${getApiEndpoint()}/pettyCash/transactionDecision`

    // Prepare the payload
    const payload = {
      transactionId: transactionId, // Use the _id as transactionId in the payload
      transactionDecision: decision
    }

    // If MSAL instance is provided, use it to get the API instance
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)
      const response = await axiosInstance.post(apiUrl, payload)
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data
    }
  } catch (error) {
    console.error(`Error ${decision}ing transaction:`, error)
    throw error
  }
}